/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { CSSProperties } from 'react';
import { Box as MuiBox, ButtonProps, styled } from '@mui/material';
import { Button } from './Button';
import TitleBox, { TitleBoxProps } from './TitleBox';
import { useNavigate } from 'react-router-dom';

interface ButtonBoxSettings {
  reverse: boolean;
  sm: CSSProperties;
  md: CSSProperties;
  lg: CSSProperties;
}

type ButtonBoxProps = Partial<ButtonBoxSettings>;

const ButtonsBox = styled('div')<ButtonBoxProps>(({ theme, reverse, sm }) => ({
  display: 'flex',
  flexDirection: reverse ? 'row-reverse' : 'row',
  gap: '1rem',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: reverse ? 'column-reverse' : 'column',
    ...sm,
  },
}));

const Box = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const IconBox = styled(Box)`
  margin-right: 8px;
`;

type BaseProps = TitleBoxProps & Pick<ButtonProps, 'color'>;

interface Props extends BaseProps {
  optionA?: {
    label: string;
    onClick?: () => void;
    link?: string;
    startIcon?: React.ReactNode;
    testId?: string;
  };
  optionB?: {
    label: string;
    onClick?: () => void;
    link?: string;
    startIcon?: React.ReactNode;
    testId?: string;
  };
  buttonBoxProps?: Partial<ButtonBoxProps>;
}
const YesNoAction = ({ optionA, optionB, buttonBoxProps, color, ...rest }: Props) => {
  const nav = useNavigate();

  const navigate = (href?: string) => {
    if (href) {
      nav(href);
    }
  };

  return (
    <Box>
      {(rest.display ?? rest.subheading ?? rest.body) && <TitleBox {...(rest as TitleBoxProps)} />}
      <ButtonsBox {...buttonBoxProps} sx={{ mt: '12px' }}>
        {optionA && (
          <Button
            variant='contained'
            color={color}
            href={optionA.link}
            onClick={(e) => {
              e.preventDefault();
              optionA.onClick?.() ?? navigate(optionA.link);
            }}
            data-testid={optionA.testId}
          >
            {optionA.startIcon && <IconBox>{optionA.startIcon}</IconBox>}
            {optionA.label}
          </Button>
        )}

        {optionB && (
          <Button
            variant='outlined'
            color={color}
            href={optionB.link}
            onClick={(e) => {
              e.preventDefault();
              optionB.onClick?.() ?? navigate(optionB.link);
            }}
            data-testid={optionB.testId}
          >
            {optionB.startIcon && <IconBox>{optionB.startIcon}</IconBox>}
            {optionB.label}
          </Button>
        )}
      </ButtonsBox>
    </Box>
  );
};

export default YesNoAction;
